/*
 * ingwer-upload
 * Ingwer-Face-Challenge: Validierung, Upload in Supabase Storage (XHR mit Fortschritt),
 * danach Insert in die Tabelle per REST. Konfiguration über data-Attribute der Section.
 */
if (!customElements.get('ingwer-upload')) {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MAX_MB = 100;
  const UPLOAD_TIMEOUT_MS = 300000;
  const INSERT_TIMEOUT_MS = 30000;
  const EXT_MIME = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    mp4: 'video/mp4',
    m4v: 'video/x-m4v',
    mov: 'video/quicktime',
    webm: 'video/webm',
    '3gp': 'video/3gpp',
  };
  const MIME_EXT = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'video/mp4': 'mp4',
    'video/quicktime': 'mov',
    'video/webm': 'webm',
    'video/3gpp': '3gp',
    'video/x-m4v': 'm4v',
  };

  class UploadError extends Error {
    constructor(message) {
      super(message);
      this.name = 'UploadError';
    }
  }

  customElements.define(
    'ingwer-upload',
    class IngwerUpload extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-igu-form]');
        if (!this.form) return;

        this.config = {
          url: (this.dataset.supabaseUrl || '').trim().replace(/\/+$/, ''),
          key: (this.dataset.supabaseKey || '').trim(),
          bucket: (this.dataset.bucket || 'ginger-faces').trim(),
          table: (this.dataset.table || 'ginger_challenge_entries').trim(),
          // Backend-Limit 100 MB, ein höherer Wert in den Section-Settings wird gekappt
          maxMb: Math.min(parseFloat(this.dataset.maxMb) || MAX_MB, MAX_MB),
        };

        this.els = {
          file: this.querySelector('[data-igu-file]'),
          drop: this.querySelector('[data-igu-drop]'),
          fileTitle: this.querySelector('[data-igu-file-title]'),
          fileHint: this.querySelector('[data-igu-file-hint]'),
          fileBtn: this.querySelector('[data-igu-file-btn]'),
          name: this.querySelector('[data-igu-name]'),
          email: this.querySelector('[data-igu-email]'),
          terms: this.querySelector('[data-igu-terms]'),
          usage: this.querySelector('[data-igu-usage]'),
          news: this.querySelector('[data-igu-news]'),
          hp: this.querySelector('[data-igu-hp]'),
          bar: this.querySelector('[data-igu-bar]'),
          barFill: this.querySelector('[data-igu-bar-fill]'),
          progressTxt: this.querySelector('[data-igu-progress-txt]'),
          status: this.querySelector('[data-igu-status]'),
          submit: this.querySelector('[data-igu-submit]'),
          success: this.querySelector('[data-igu-success]'),
        };

        this.defaults = {
          fileTitle: this.els.fileTitle ? this.els.fileTitle.textContent : '',
          fileHint: this.els.fileHint ? this.els.fileHint.textContent : '',
        };

        this.file = null;
        this.uploaded = null; // { file, path } – nach erfolgreichem Upload, damit ein Retry nur den Insert wiederholt
        this.busy = false;

        this.els.file.addEventListener('change', () => {
          this.setFile(this.els.file.files && this.els.file.files[0]);
        });
        this.initDrop();

        [this.els.name, this.els.email].forEach((input) => {
          input.addEventListener('input', () => this.clearError(input === this.els.name ? 'name' : 'email', input));
        });
        [this.els.terms, this.els.usage].forEach((input) => {
          input.addEventListener('change', () => this.clearError(input === this.els.terms ? 'terms' : 'usage', input));
        });

        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.onSubmit();
        });
      }

      initDrop() {
        const drop = this.els.drop;
        if (!drop) return;
        ['dragenter', 'dragover'].forEach((type) => {
          drop.addEventListener(type, (event) => {
            event.preventDefault();
            if (!this.busy) drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'dragend'].forEach((type) => {
          drop.addEventListener(type, () => drop.classList.remove('is-dragover'));
        });
        drop.addEventListener('drop', (event) => {
          event.preventDefault();
          drop.classList.remove('is-dragover');
          if (this.busy) return;
          const files = event.dataTransfer && event.dataTransfer.files;
          if (files && files[0]) this.setFile(files[0]);
        });
      }

      /* ---------- Datei ---------- */

      setFile(file) {
        this.file = file || null;
        this.clearError('file');
        this.setStatus('');

        if (!this.file) {
          this.els.fileTitle.textContent = this.defaults.fileTitle;
          this.els.fileHint.textContent = this.defaults.fileHint;
          if (this.els.fileBtn) this.els.fileBtn.textContent = 'Datei wählen';
          this.setState('idle');
          return;
        }

        this.els.fileTitle.textContent = this.file.name;
        this.els.fileHint.textContent = `${this.formatSize(this.file.size)}. Du kannst die Datei noch ändern.`;
        if (this.els.fileBtn) this.els.fileBtn.textContent = 'Andere Datei wählen';
        this.setState('selected');

        const fileError = this.validateFile(this.file);
        if (fileError) this.showError('file', fileError);
      }

      mimeOf(file) {
        if (file.type) return file.type.toLowerCase();
        const ext = this.extOf(file.name);
        return EXT_MIME[ext] || '';
      }

      extOf(name) {
        const match = /\.([a-z0-9]{1,5})$/i.exec(name || '');
        return match ? match[1].toLowerCase() : '';
      }

      validateFile(file) {
        if (!file) return 'Bitte wähle ein Foto oder Video aus.';
        const mime = this.mimeOf(file);
        if (!/^(image|video)\//.test(mime)) return 'Bitte lade ein Foto oder Video hoch. Andere Dateitypen können wir nicht annehmen.';
        if (file.size === 0) return 'Die Datei ist leer. Bitte wähle eine andere Datei.';
        if (file.size > this.config.maxMb * 1024 * 1024) {
          return `Die Datei ist ${this.formatSize(file.size)} groß. Erlaubt sind maximal ${this.config.maxMb} MB. Kürze Dein Video oder wähle eine kleinere Datei.`;
        }
        return '';
      }

      formatSize(bytes) {
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      }

      /* ---------- Validierung ---------- */

      validate() {
        const errors = [];
        const fileError = this.validateFile(this.file);
        if (fileError) errors.push(['file', fileError, this.els.file]);

        const name = this.els.name.value.trim();
        if (name.length < 2) errors.push(['name', 'Bitte gib Deinen Namen ein.', this.els.name]);

        const email = this.els.email.value.trim();
        if (!email) errors.push(['email', 'Bitte gib Deine E-Mail-Adresse ein.', this.els.email]);
        else if (!EMAIL_RE.test(email)) errors.push(['email', 'Bitte prüfe Deine E-Mail-Adresse.', this.els.email]);

        if (!this.els.terms.checked) {
          errors.push(['terms', 'Bitte akzeptiere die Teilnahmebedingungen.', this.els.terms]);
        }
        if (!this.els.usage.checked) {
          errors.push(['usage', 'Bitte stimme der Nutzung Deines Bildes oder Videos zu.', this.els.usage]);
        }

        ['file', 'name', 'email', 'terms', 'usage'].forEach((key) => this.clearError(key));
        errors.forEach(([key, message, input]) => this.showError(key, message, input));
        return errors;
      }

      showError(key, message, input) {
        const el = this.querySelector(`[data-igu-err="${key}"]`);
        if (el) el.textContent = message;
        const target = input || (key === 'file' ? this.els.file : null);
        if (target) target.setAttribute('aria-invalid', 'true');
        if (key === 'file' && this.els.drop) this.els.drop.classList.add('is-invalid');
      }

      clearError(key, input) {
        const el = this.querySelector(`[data-igu-err="${key}"]`);
        if (el) el.textContent = '';
        const target = input || (key === 'file' ? this.els.file : null);
        if (target) target.removeAttribute('aria-invalid');
        if (key === 'file' && this.els.drop) this.els.drop.classList.remove('is-invalid');
      }

      /* ---------- Zustand ---------- */

      setState(state) {
        this.dataset.state = state;
      }

      setStatus(message) {
        this.els.status.textContent = message;
      }

      setBusy(busy) {
        this.busy = busy;
        this.els.submit.disabled = busy;
        this.els.file.disabled = busy;
        this.form.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      setProgress(percent, text) {
        const value = Math.max(0, Math.min(100, Math.round(percent)));
        this.els.barFill.style.width = `${value}%`;
        this.els.bar.setAttribute('aria-valuenow', String(value));
        if (text) this.els.progressTxt.textContent = text;
      }

      /* ---------- Submit ---------- */

      async onSubmit() {
        if (this.busy) return;
        this.setStatus('');

        const errors = this.validate();
        if (errors.length) {
          const first = errors[0];
          this.setStatus(errors.length > 1 ? 'Bitte prüfe die markierten Angaben.' : first[1]);
          (first[0] === 'file' ? this.els.file : first[2]).focus();
          return;
        }

        // Honeypot gefüllt: Bot. Erfolg vortäuschen, nichts senden.
        if (this.els.hp && this.els.hp.value) {
          this.showSuccess(null);
          return;
        }

        if (!this.config.url || !this.config.key) {
          this.setStatus('Die Teilnahme ist gerade nicht möglich. Bitte versuche es später noch einmal.');
          return;
        }

        this.setBusy(true);
        this.setState('uploading');
        this.setProgress(0, 'Upload läuft … 0 %');

        const file = this.file;
        const mime = this.mimeOf(file);

        try {
          let path;
          if (this.uploaded && this.uploaded.file === file) {
            path = this.uploaded.path;
            this.setProgress(100, 'Datei ist schon hochgeladen. Teilnahme wird gespeichert …');
          } else {
            const ext = this.extOf(file.name) || MIME_EXT[mime] || 'bin';
            path = `${this.uuid()}.${ext}`;
            await this.uploadFile(file, path, mime);
            this.uploaded = { file, path };
            this.setProgress(100, 'Upload fertig. Teilnahme wird gespeichert …');
          }

          await this.insertEntry({
            name: this.els.name.value.trim(),
            email: this.els.email.value.trim(),
            file_path: path,
            mime_type: mime,
            file_size: file.size,
            consent_terms: this.els.terms.checked,
            consent_usage: this.els.usage.checked,
            newsletter_opt_in: !!(this.els.news && this.els.news.checked),
            source: 'web',
          });

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'ginger_challenge_upload',
            file_type: mime.indexOf('video/') === 0 ? 'video' : 'image',
          });

          this.showSuccess();
        } catch (error) {
          const message =
            error instanceof UploadError
              ? error.message
              : 'Da ist etwas schiefgelaufen. Bitte versuche es noch einmal.';
          this.setBusy(false);
          this.setState('error');
          this.setStatus(message);
          this.els.submit.textContent = 'Erneut versuchen';
          this.els.submit.focus();
        }
      }

      uploadFile(file, path, mime) {
        const { url, key, bucket } = this.config;
        const endpoint = `${url}/storage/v1/object/${encodeURIComponent(bucket)}/${encodeURIComponent(path)}`;

        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', endpoint, true);
          xhr.timeout = UPLOAD_TIMEOUT_MS;
          xhr.setRequestHeader('apikey', key);
          xhr.setRequestHeader('Authorization', `Bearer ${key}`);
          xhr.setRequestHeader('Content-Type', mime);

          xhr.upload.addEventListener('progress', (event) => {
            if (!event.lengthComputable) return;
            const percent = (event.loaded / event.total) * 100;
            this.setProgress(percent, `Upload läuft … ${Math.round(percent)} %`);
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
              return;
            }
            const body = xhr.responseText || '';
            if (xhr.status === 413 || /"413"|too large|exceeded the maximum/i.test(body)) {
              reject(new UploadError('Die Datei ist zu groß für den Upload. Bitte wähle eine kleinere Datei.'));
            } else if (xhr.status === 415 || /mime type|invalid_mime/i.test(body)) {
              reject(new UploadError('Dieses Dateiformat können wir leider nicht annehmen. Bitte versuche es mit JPG, PNG, MP4 oder MOV.'));
            } else {
              reject(new UploadError('Der Upload hat nicht geklappt. Bitte versuche es noch einmal.'));
            }
          });
          xhr.addEventListener('error', () => {
            reject(new UploadError('Keine Verbindung. Bitte prüfe Dein Internet und versuche es noch einmal.'));
          });
          xhr.addEventListener('timeout', () => {
            reject(new UploadError('Der Upload hat zu lange gedauert. Bitte versuche es mit einer kleineren Datei oder besserem Empfang noch einmal.'));
          });
          xhr.addEventListener('abort', () => {
            reject(new UploadError('Der Upload wurde abgebrochen. Bitte versuche es noch einmal.'));
          });

          xhr.send(file);
        });
      }

      async insertEntry(payload) {
        const { url, key, table } = this.config;
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), INSERT_TIMEOUT_MS) : null;

        let response;
        try {
          response = await fetch(`${url}/rest/v1/${encodeURIComponent(table)}`, {
            method: 'POST',
            headers: {
              apikey: key,
              Authorization: `Bearer ${key}`,
              'Content-Type': 'application/json',
              Prefer: 'return=minimal',
            },
            body: JSON.stringify(payload),
            signal: controller ? controller.signal : undefined,
          });
        } catch (error) {
          throw new UploadError(
            'Deine Datei ist angekommen, aber die Teilnahme konnte nicht gespeichert werden. Bitte prüfe Dein Internet und tippe auf „Erneut versuchen“.'
          );
        } finally {
          if (timer) clearTimeout(timer);
        }

        if (!response.ok) {
          throw new UploadError(
            'Deine Datei ist angekommen, aber die Teilnahme konnte nicht gespeichert werden. Bitte tippe auf „Erneut versuchen“.'
          );
        }
      }

      showSuccess() {
        this.setBusy(false);
        this.setState('success');
        this.form.hidden = true;
        this.els.success.hidden = false;
        this.els.success.focus();
      }

      uuid() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
        const bytes = new Uint8Array(16);
        if (window.crypto && window.crypto.getRandomValues) {
          window.crypto.getRandomValues(bytes);
        } else {
          for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
        }
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }
    }
  );
}
